import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { routerNavigatedAction, routerRequestAction } from '@ngrx/router-store';
import { Store, select } from '@ngrx/store';
import { EMPTY, Observable, defer, fromEvent, iif, interval, merge, of } from 'rxjs';
import { bufferToggle, concatMap, delay, distinctUntilChanged, filter, first, map } from 'rxjs/operators';
import { catchError, pairwise, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { BreadcrumbItem } from 'ish-core/models/breadcrumb-item/breadcrumb-item.interface';
import { selectRouteData } from 'ish-core/store/core/router';
import { whenTruthy } from 'ish-core/utils/operators';

import {
  setBreadcrumbData,
  setBreadcrumbDataFromRouting,
  setHeaderHeight,
  setIsTransparentHeader,
  setStickyHeader,
} from './viewconf.actions';
import { getHeaderHeight, isStickyHeader } from './viewconf.selectors';

@Injectable()
export class ViewconfEffects {
  constructor(
    private store: Store,
    private actions$: Actions
  ) {}

  //commented for the purpose of new header to do uncomment if the old header is needed
  //Update StickyHeader based on FeatureToggleService in 6.0.0 version
  /*  toggleStickyHeader$ =
    !SSR &&
    createEffect(() =>
      this.featureToggleService.enabled$('stickyHeader').pipe(
        whenTruthy(),
        concatMap(() =>
          defer(() =>
            fromEvent(window, 'scroll').pipe(
              map(() => window.scrollY >= 170),
              distinctUntilChanged(),
              map(sticky => setStickyHeader({ sticky }))
            )
          )
        )
      )
    );*/

  retrieveBreadcrumbDataFromRouting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setBreadcrumbData),
      // collect all breadcrumb actions during routing
      bufferToggle(this.actions$.pipe(ofType(routerRequestAction)), () =>
        this.actions$.pipe(ofType(routerNavigatedAction), delay(10))
      ),
      // if no breadcrumb was set with effects
      filter(actions => !actions.length),
      concatMap(() =>
        // set the current one from the routing (if available)
        this.store.pipe(
          select(selectRouteData<BreadcrumbItem[]>('breadcrumbData')),
          first(),
          whenTruthy(),
          map(breadcrumbData => setBreadcrumbDataFromRouting({ breadcrumbData }))
        )
      )
    )
  );

  /*   DIAS Customization Start */
  toggleStickyHeader$ =
    !SSR &&
    createEffect(() =>
      defer(() =>
        this.store.pipe(
          select(getHeaderHeight), // Get the header height from the store
          switchMap(headerHeight =>
            merge(fromEvent(window, 'scroll'), fromEvent(window, 'resize')).pipe(
              map(() => ({
                scrollY: window.scrollY || document.documentElement.scrollTop,
                documentHeight: document.body.clientHeight,
                headerHeight, // Use the header height from the store
              })),
              pairwise(),
              distinctUntilChanged((prev, curr) => prev[0].scrollY === curr[0].scrollY), // Only emit if scrollY value changes
              map(([_, curr]) => {
                // const { scrollY: prevScroll, documentHeight: prevDocumentHeight } = prev;
                // const { scrollY: currScroll, documentHeight: currDocumentHeight } = curr;
                const { scrollY: currScroll } = curr;
                if (currScroll <= headerHeight || currScroll < 50) {
                  return setStickyHeader({ sticky: false });
                } else {
                  return setStickyHeader({ sticky: true });
                }

                // if (currScroll <= headerHeight) {
                //   return setStickyHeader({ sticky: false });
                // } else {
                //   if (prevScroll >= currScroll) {
                //     if (prevDocumentHeight === currDocumentHeight) {
                //       return setStickyHeader({ sticky: true });
                //     } else {
                //       return maintainStickyHeaderState();
                //     }
                //   } else {
                //     if (prevDocumentHeight === currDocumentHeight) {
                //       return setStickyHeader({ sticky: false });
                //     } else {
                //       return maintainStickyHeaderState();
                //     }
                //   }
                // }
              })
            )
          )
        )
      )
    );

  headerHeight$ = createEffect(() =>
    iif(
      () => !SSR,
      defer(() =>
        merge(
          fromEvent(window, 'load'),
          fromEvent(window, 'resize') //Trigger on window resize
        ).pipe(
          switchMap(() =>
            interval(100).pipe(
              switchMap(() => {
                const header = document.getElementById('header') as HTMLElement;
                if (header) {
                  return of(header);
                } else {
                  return EMPTY; // If header is not available, emit empty observable
                }
              }),
              filter(header => !!header), // Wait until header becomes truthy
              take(1), // Take only the first occurrence
              switchMap(
                header =>
                  new Observable<number>(observer => {
                    const observerCallback = () => {
                      const headerHeight = header.offsetHeight;
                      observer.next(headerHeight);
                    };

                    observerCallback(); // Call once to get initial height

                    const resizeObserver = new ResizeObserver(observerCallback);
                    resizeObserver.observe(header);

                    return () => {
                      resizeObserver.disconnect();
                    };
                  })
              ),
              switchMap(headerHeight => {
                if (headerHeight > 0) {
                  return of(headerHeight);
                } else {
                  // Continuously check the header height until it becomes non-zero
                  return interval(100).pipe(
                    switchMap(() => {
                      const newHeaderHeight = (document.getElementById('header') as HTMLElement)?.offsetHeight;
                      if (newHeaderHeight > 0) {
                        return of(newHeaderHeight);
                      } else {
                        return EMPTY;
                      }
                    })
                  );
                }
              })
            )
          ),
          withLatestFrom(this.store.pipe(select(getHeaderHeight)), this.store.pipe(select(isStickyHeader))), // Combine with previous header height
          map(([headerHeight, prevHeight, isSticky]) => {
            if (!isSticky && headerHeight !== prevHeight && headerHeight > 100) {
              return setHeaderHeight({ headerHeight });
            } else {
              return { type: 'NOOP_ACTION' };
            }
          }),
          filter(action => action.type !== 'NOOP_ACTION'), // Filter out noop actions
          catchError(() => of({ type: 'Failed to update header height' }))
        )
      ),
      EMPTY
    )
  );

  // Reset transparent header on every route navigation
  resetTransparentHeaderOnRouteChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      map((action: ReturnType<typeof routerNavigatedAction>) => action.payload?.routerState?.url ?? ''),
      pairwise(),
      filter(([prevUrl, currUrl]) => prevUrl !== currUrl),
      map(() => setIsTransparentHeader({ isTransparentHeader: false }))
    )
  );

  /* DIAS Customization End*/
}
